import * as THREE from 'three'
import { clamp01 } from '../core/math'
import { radialTexture } from './textures'
import { HAZE } from './atmosphere'

/** Airborne snow is lit by the same two sources as the ground it came from. */
export const SPRAY_SUN = { value: new THREE.Color(0.66, 0.63, 0.57) }
export const SPRAY_SKY = { value: new THREE.Color(0.28, 0.34, 0.45) }

/**
 * Snow spray off the tails. Cosmetic, but Phase 1's gate is "is it fun to just
 * go down" and the single strongest speed cue on a small screen is snow moving
 * past the camera. It also does real communication work: the plume is much
 * bigger in powder than on wind slab, so you can see which surface you are on
 * without reading the debug overlay.
 */

const MAX_PARTICLES = 900

export class Spray {
  readonly points: THREE.Points
  private readonly pos: Float32Array
  private readonly vel: Float32Array
  private readonly life: Float32Array
  private readonly maxLife: Float32Array
  private readonly size: Float32Array
  private readonly alpha: Float32Array
  private readonly geom: THREE.BufferGeometry
  private cursor = 0
  private carry = 0
  /** Deterministic-enough jitter; visuals only, never fed back into the sim. */
  private seed = 0x2545f491

  constructor() {
    this.pos = new Float32Array(MAX_PARTICLES * 3)
    this.vel = new Float32Array(MAX_PARTICLES * 3)
    this.life = new Float32Array(MAX_PARTICLES)
    this.maxLife = new Float32Array(MAX_PARTICLES)
    this.size = new Float32Array(MAX_PARTICLES)
    this.alpha = new Float32Array(MAX_PARTICLES)

    // Park unused particles far below the world rather than branching in the
    // shader; they cost nothing and never show.
    for (let i = 0; i < MAX_PARTICLES; i++) this.pos[i * 3 + 1] = -10000

    this.geom = new THREE.BufferGeometry()
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3))
    this.geom.setAttribute('size', new THREE.BufferAttribute(this.size, 1))
    this.geom.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1))
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6)

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: radialTexture(48, 0.35) },
        uSunDir: HAZE.sunDir,
        uSunCol: SPRAY_SUN,
        uSkyCol: SPRAY_SKY,
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / max(-mv.z, 1.0));
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform sampler2D map;
        uniform vec3 uSunDir;
        uniform vec3 uSunCol;
        uniform vec3 uSkyCol;
        varying float vAlpha;
        void main() {
          vec4 t = texture2D(map, gl_PointCoord);
          // Fake a spherical normal from the sprite coordinate so each particle
          // has volume rather than reading as a flat dot, then light it from the
          // same two sources as the ground it came off. Pure white spray was one
          // of the last things making the scene look diagrammatic — airborne
          // snow goes blue in shade exactly like the snowpack does.
          vec2 d = gl_PointCoord * 2.0 - 1.0;
          float r2 = dot(d, d);
          if (r2 > 1.0) discard;
          vec3 n = normalize(vec3(d, sqrt(max(0.0, 1.0 - r2))));
          float lambert = max(dot(n, uSunDir), 0.0);
          vec3 lit = uSkyCol + uSunCol * (0.35 + 0.65 * lambert);
          gl_FragColor = vec4(lit, t.a * vAlpha);
          if (gl_FragColor.a < 0.01) discard;
        }`,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    })

    this.points = new THREE.Points(this.geom, mat)
    this.points.frustumCulled = false
    this.points.renderOrder = 2
  }

  private rand(): number {
    // xorshift32 — cheap, and keeps Math.random() out of the codebase entirely.
    let s = this.seed
    s ^= s << 13
    s ^= s >>> 17
    s ^= s << 5
    this.seed = s >>> 0
    return this.seed / 4294967296
  }

  /**
   * @param rate  particles per second (from snow type × speed × skid)
   * @param power 0..1 overall energy — drives size, spread and lifetime
   */
  emit(
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number,
    rate: number,
    power: number,
    dt: number,
  ): void {
    this.carry += rate * dt
    let n = Math.floor(this.carry)
    this.carry -= n
    if (n > 40) n = 40

    for (let k = 0; k < n; k++) {
      const i = this.cursor
      this.cursor = (this.cursor + 1) % MAX_PARTICLES
      const o = i * 3
      const spread = 0.35 + power * 0.8
      this.pos[o] = x + (this.rand() - 0.5) * spread
      this.pos[o + 1] = y + 0.05 + this.rand() * 0.25
      this.pos[o + 2] = z + (this.rand() - 0.5) * spread
      // Thrown back and up out of the track.
      const back = 0.18 + this.rand() * 0.3
      this.vel[o] = -vx * back + (this.rand() - 0.5) * 3.2 * power
      this.vel[o + 1] = -vy * 0.15 + 1.4 + this.rand() * 3.4 * power
      this.vel[o + 2] = -vz * back + (this.rand() - 0.5) * 3.2 * power
      const life = 0.35 + this.rand() * (0.5 + power * 0.75)
      this.life[i] = life
      this.maxLife[i] = life
      this.size[i] = 0.5 + this.rand() * (0.8 + power * 2.6)
      this.alpha[i] = 0
    }
  }

  update(dt: number): void {
    const { pos, vel, life, maxLife, alpha } = this
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (life[i] <= 0) continue
      life[i] -= dt
      if (life[i] <= 0) {
        pos[i * 3 + 1] = -10000
        alpha[i] = 0
        continue
      }
      const o = i * 3
      // Light snow: it hangs, it doesn't fall like gravel.
      vel[o + 1] -= 5.2 * dt
      const drag = 1 - Math.min(1, 2.4 * dt)
      vel[o] *= drag
      vel[o + 1] *= drag
      vel[o + 2] *= drag
      pos[o] += vel[o] * dt
      pos[o + 1] += vel[o + 1] * dt
      pos[o + 2] += vel[o + 2] * dt
      const t = life[i] / maxLife[i]
      alpha[i] = clamp01(t * t * 0.85)
    }
    this.geom.attributes.position.needsUpdate = true
    this.geom.attributes.size.needsUpdate = true
    this.geom.attributes.alpha.needsUpdate = true
  }

  clear(): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.life[i] = 0
      this.alpha[i] = 0
      this.pos[i * 3 + 1] = -10000
    }
    this.geom.attributes.position.needsUpdate = true
    this.geom.attributes.alpha.needsUpdate = true
  }
}
